import { isCustomerRole } from './user-role'

/**
 * Single source for "should this user's activity be excluded from analytics
 * reports?" — imported by both the API (user service output) and the web app
 * (the PostHog/GA4 provider), so the derivation is declared once.
 *
 * The exclusion is a per-user tri-state, stored as a nullable boolean on the
 * user row (`analytics_excluded`):
 *   - `null`        → Auto: derive from role — excluded for any non-customer.
 *   - `true`/`false`→ explicit override, applies to any user regardless of role.
 *
 * "Non-customer" (staff/driver) is the sensible default for internal accounts,
 * but the override is what covers the cases a role rule can't: third-party
 * contractors, demo accounts, and App Store / Play review logins (which are
 * usually `role = 'customer'`). Those are excluded by setting the override to
 * true, without touching the account's role.
 */

/**
 * The roles whose activity counts as real customer traffic by default.
 *
 * A SET, not a single role: the previous single-value form meant that adding
 * any new customer-kind role silently reclassified those users as internal
 * staff and dropped them from every analytics report — no error, no signal, and
 * only discoverable by noticing a segment had zero traffic.
 */
export { CUSTOMER_ROLES } from './user-role'

/**
 * Resolve the effective analytics-exclusion for a user.
 *
 * @param role     The user's role. Customer-kind roles (see CUSTOMER_ROLES)
 *                 count as real traffic; everything else is internal.
 *                 The DB column is non-null (defaults to 'customer'); a missing
 *                 value here (loose client session typing) is treated as a
 *                 customer, so Auto never excludes on an unknown role.
 * @param override The stored per-user override: `null`/`undefined` = Auto.
 * @returns `true` when the user should be excluded from analytics reports.
 */
export function resolveAnalyticsExcluded(
  role: string | null | undefined,
  override: boolean | null | undefined,
): boolean {
  return override ?? (role != null && !isCustomerRole(role))
}
